import { useEffect, useState } from "react";
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Modal,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import "./AgencyProfileAgentInfo.scss";
import { AgentApi } from "../../../utils/api/Agent";
import { clientRoutes } from "../../../utils/routes";
import { messages } from "../../../config/messages";
import { AgencyApi } from "../../../utils/api/Agency";
import { LocalStorageHelpers } from "../../../utils/helpers/localstorage.helpers";
import ProtectedComponent from "../../../components/ProtectedComponent";
import CustomPageLoader from "../../../components/loaders/CustomPageLoader";
import { CommonEnums } from "../../../enums/common.enums";
import AgentProfilePicture from "../AgentProfilePicture";
import AgentErrorSummary from "../../agencyProfile/AgentErrorSummary";
import AgentCreatingAgencySteps from "./AgencyProfileSteps";

const UpdateAgencyAgentInfo = () => {
  const navigate = useNavigate();
  const [profileImage, setProfileImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const openLogoutModal = () => setIsLogoutModalOpen(true);
  const closeLogoutModal = () => setIsLogoutModalOpen(false);

  const [state, setState] = useState<{
    agencyData: any | null;
    loadingAgencyData: boolean;
  }>({
    agencyData: null,
    loadingAgencyData: true,
  });

  const initialValues = {
    firstName: "",
    lastName: "",
    agent_email: "",
    mobileNo: "",
    mobileCode: "+61",
    designation: "",
    profileImageLive: "",
    profileImageUploadRecord: null,
  };

  const onSubmitHandler = async (values: any) => {
    setIsLoading(true);

    const principalAgentId = state.agencyData?.principal_agent_id?._id;
    const agencyId = state.agencyData?._id;

    if (!principalAgentId || !agencyId) {
      toast.error("Agency or Agent ID is missing. Please try again.");
      setIsLoading(false);
      return;
    }

    try {
      const agentFormData = new FormData();
      agentFormData.append("designation", values?.designation);
      if (profileImage) {
        agentFormData.append("profile_image", profileImage);
      }
      agentFormData.append(
        "registration_status",
        CommonEnums.agentRegistrationStatus.completed
      );

      const agentRes = await AgentApi.updateAgentProfile(agentFormData, principalAgentId);

      if (agentRes.error || !agentRes.data?.success) {
        throw new Error(
          agentRes.data?.message || "Failed to update agent profile."
        );
      }

      const agencyFormData = new FormData();
      agencyFormData.append(
        "status",
        CommonEnums.status.pending_for_admin_approval
      );
      agencyFormData.append(
        "registration_status",
        CommonEnums.agentRegistrationStatus.completed
      );

      const agencyRes = await AgencyApi.updateAgencyProfile(agencyFormData, agencyId);

      if (!agencyRes?.data?.success) {
        throw new Error(
          agencyRes.data?.message || "Failed to re-submit agency for approval."
        );
      }

      toast.success("Application re-submitted for approval successfully.");
      navigate(clientRoutes.adminApprovalWaiting);

    } catch (error: any) {
      console.error("Submission Error:", error);
      toast.error(error.message || "An unexpected error occurred.");
    } finally {
      setIsLoading(false);
    }
  };

  // const onSubmitHandler = async (values: any) => {
  //   setIsLoading(true);

  //   const formData = new FormData();
  //   formData.append("designation", values?.designation);
  //   if (profileImage) {
  //     formData.append("profile_image", profileImage);
  //   }

  //   const principalAgentId = state.agencyData?.principal_agent_id?._id;

  //   if (!principalAgentId) {
  //     toast.error("Principal Agent ID not found. Cannot update profile.");
  //     setIsLoading(false);
  //     return;
  //   }

  //   const res = await AgentApi.updateAgentProfile(formData, principalAgentId);

  //   if (!res.error && res.data?.success) {
  //     toast.success("Agent profile updated successfully.");
  //     navigate(clientRoutes.adminApprovalWaiting);
  //   } else {
  //     toast.error(
  //       res?.data?.message ??
  //         "Something went wrong while updating agent profile. Please try again.",
  //     );
  //   }

  //   setIsLoading(false);
  // };

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      firstName: Yup.string().required(messages?.firstNameIsRequired),
      lastName: Yup.string().required(messages?.lastNameIsRequired),
      agent_email: Yup.string()
        .email("Enter your valid email address!")
        .required("Please enter your email!"),
      mobileNo: Yup.string().required(messages?.phoneNumberIsRequired),
      mobileCode: Yup.string().required(messages?.phoneNumberIsRequired),
      designation: Yup.string().required(messages?.errorMessage),
      profileImageLive: Yup.string().required("Profile photo is required!"),
    }),
    onSubmit: onSubmitHandler,
    validateOnChange: false,
    validateOnBlur: false,
    validateOnMount: false,
  });

  const getDetails = async () => {
    setState((prevState) => ({ ...prevState, loadingAgencyData: true }));

    const agencyRes = await AgencyApi.getAgencyDetails();
    if (!agencyRes.success) {
      toast.error(
        agencyRes?.data?.message ?? "Failed to fetch agency details.",
      );
      setState((prevState) => ({ ...prevState, loadingAgencyData: false }));
      navigate(clientRoutes.login);
      return;
    }

    const agencyData = agencyRes.data?.data;

    LocalStorageHelpers.setAgencyData({ data: agencyData });

    const principalAgent = agencyData?.principal_agent_id || {};

    formik.setFieldValue("agent_email", agencyData?.email || "");
    formik.setFieldValue(
      "firstName",
      agencyData?.principal_agent_first_name || "",
    );
    formik.setFieldValue(
      "lastName",
      agencyData?.principal_agent_last_name || "",
    );
    formik.setFieldValue(
      "mobileNo",
      agencyData?.principal_agent_mobile_number || "",
    );
    formik.setFieldValue(
      "mobileCode",
      agencyData?.phone_country_code
        ? `+${agencyData.phone_country_code}`
        : "+61",
    );

    formik.setFieldValue("designation", principalAgent?.designation || "");

    if (principalAgent?.profile_image_ref) {
      formik.setFieldValue(
        "profileImageUploadRecord",
        principalAgent.profile_image_ref,
      );
      formik.setFieldValue(
        "profileImageLive",
        principalAgent.profile_image_ref.storage_key ||
          principalAgent.profile_image_ref.original_file_path,
      );
    } else if (principalAgent?.profile_image) {
      formik.setFieldValue("profileImageLive", principalAgent.profile_image);
    }

    setState({
      agencyData: agencyData,
      loadingAgencyData: false,
    });
  };

  const handleLogoutConfirm = () => {
    closeLogoutModal();
    LocalStorageHelpers.clear();
    navigate(clientRoutes.login);
  };

  const handleBack = () => {
    navigate(clientRoutes.agentCreateAgencyBrandGrammar);
  };

  useEffect(() => {
    getDetails();
  }, []);

  if (state.loadingAgencyData) {
    return (
      <ProtectedComponent>
        <div className="agent-profile-wrapper">
          <div className="circular-progress-container">
            <CustomPageLoader />
          </div>
        </div>
      </ProtectedComponent>
    );
  }

  return (
    <ProtectedComponent>
      <AgentCreatingAgencySteps step={CommonEnums.agencyProfileSteps.agentInfo} agencyData={state.agencyData} />
      <div className="agent-profile-wrapper">
        <div className="form-content-wrapper">
          <div className="section-header">
            <h3>Principal Agent Info</h3>
            <p className="subtitle">
              Update the details for the person who will manage this account.
            </p>
          </div>
          <form className="form-container" onSubmit={formik.handleSubmit}>
            <div className="double-item-container">
              <div className="input-and-lable-container">
                <p className="label font-inter-400">Principal First Name*</p>
                <TextField
                  placeholder="Enter First Name"
                  variant="outlined"
                  fullWidth
                  className="gray-bg-input"
                  value={formik?.values?.firstName}
                  onChange={formik?.handleChange}
                  name="firstName"
                  disabled={true}
                  error={Boolean(formik?.errors?.firstName)}
                />
              </div>
              <div className="input-and-lable-container">
                <p className="label font-inter-400">Principal Last Name*</p>
                <TextField
                  placeholder="Enter Last Name"
                  variant="outlined"
                  fullWidth
                  className="gray-bg-input"
                  value={formik?.values?.lastName}
                  onChange={formik?.handleChange}
                  name="lastName"
                  disabled={true}
                  error={Boolean(formik?.errors?.lastName)}
                />
              </div>
            </div>
            <div className="double-item-container">
              <div className="input-and-lable-container">
                <p className="label font-inter-400">Principal Work Email*</p>
                <TextField
                  placeholder="Enter Work Email"
                  variant="outlined"
                  fullWidth
                  disabled={true}
                  className="gray-bg-input disabled-input"
                  value={formik?.values?.agent_email}
                  onChange={formik?.handleChange}
                  name="agent_email"
                  error={Boolean(formik?.errors?.agent_email)}
                />
              </div>
              <div className="input-and-lable-container">
                <p className="label font-inter-400">Principal Work Mobile*</p>
                <div className="unified-phone-input">
                  <div className="code-picker">
                    <FormControl variant="outlined" fullWidth>
                      <Select
                        onChange={formik?.handleChange}
                        name="mobileCode"
                        value={formik?.values?.mobileCode}
                        disabled={true}
                        MenuProps={{
                          disablePortal: true,
                          classes: { paper: "selector-menu-container" },
                        }}
                        variant="standard"
                        disableUnderline
                        displayEmpty
                      >
                        <MenuItem value="+61">+61</MenuItem>
                        <MenuItem value="+91">+91</MenuItem>
                        <MenuItem value="+64">+64</MenuItem>
                        <MenuItem value="+1">+1</MenuItem>
                        <MenuItem value="+44">+44</MenuItem>
                      </Select>
                    </FormControl>
                  </div>
                  <TextField
                    placeholder="Enter Mobile"
                    variant="outlined"
                    className="mobile-input-internal"
                    type="text"
                    disabled={true}
                    value={formik?.values?.mobileNo}
                    onChange={formik?.handleChange}
                    name="mobileNo"
                    error={Boolean(formik?.errors?.mobileNo)}
                  />
                </div>
              </div>
            </div>
            <div className="single-item-container">
              <div className="input-and-lable-container">
                <p className="label font-inter-400">
                  Designation At Your Agency*
                </p>
                <TextField
                  variant="outlined"
                  placeholder="Enter Designation at Your Agency"
                  className="gray-bg-input"
                  value={formik?.values?.designation}
                  onChange={formik?.handleChange}
                  name="designation"
                  error={Boolean(formik?.errors?.designation)}
                />
              </div>
            </div>
            <div className="photo-upload-section">
              <h3 className="section-title">Profile Photo</h3>
              <p className="section-subtitle">
                Add a photo to help clients and teammates recognize you
              </p>
              <div className="photo-box-wrapper">
                <AgentProfilePicture
                  profileImage={profileImage}
                  setProfileImage={setProfileImage}
                  formik={formik}
                />
              </div>
            </div>
            {Object.keys(formik.errors).length > 0 && (
              <AgentErrorSummary errors={formik.errors} />
            )}
            <div className="action-buttons">
              <button
                type="button"
                className="logout-btn font-inter-400"
                onClick={handleBack}
              >
                Back
              </button>
              <button
                className="continue-btn font-inter-500"
                disabled={isLoading}
                type="submit"
              >
                {isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Save & continue"
                )}
              </button>
            </div>
            <div className="form-footer">
              <p>
                By continuing, you agree to our <a>Terms and Conditions</a>
              </p>
              <p>
                Read our <a>Personal Information Collection Statement</a>
              </p>
              <br />
              <p>Profile progress saved.</p>
              <p>
                Feel free to log out; you can continue your setup at any time.
              </p>
              <p>
                <a
                  onClick={openLogoutModal}
                  style={{ textDecoration: "underline", cursor: "pointer" }}
                >
                  Logout
                </a>
              </p>
            </div>
          </form>
        </div>
      </div>
      <Modal
        open={isLogoutModalOpen}
        onClose={closeLogoutModal}
        aria-labelledby="logout-modal-title"
        aria-describedby="logout-modal-desc"
        className="logout-confirm-modal"
      >
        <div className="logout-modal-center">
          <div className="logout-modal-card">
            <h3 id="logout-modal-title" className="logout-modal-title">
              Log out?
            </h3>
            <p id="logout-modal-desc" className="logout-modal-subtitle">
              Unsaved changes will be discarded.
              <br />
              Are you sure you want to proceed?
            </p>
            <div className="logout-modal-actions">
              <button
                type="button"
                className="logout-cancel-btn"
                onClick={closeLogoutModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="logout-confirm-btn"
                onClick={handleLogoutConfirm}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        open={isLoading}
        disableEscapeKeyDown
        aria-labelledby="processing-modal"
        className="processing-modal agent-info-modal"
      >
        <div className="modal-center-wrapper">
          <div className="loading-content-wrapper">
            <div className="loading-container">
              <div className="spinner" />
              <p className="loading-text">
                Processing your request
                <span className="desktop-break">
                  <br />
                </span>
                Please wait...
              </p>
            </div>
          </div>
        </div>
      </Modal>
    </ProtectedComponent>
  );
};

export default UpdateAgencyAgentInfo;
